import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

import { authService } from '@/services/authService';
import { User, LoginCredentials } from '@/types/auth';
import { defaultHomePath } from '@/utils/access';

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

type AuthAction =
  | { type: 'LOGIN_START' }
  | { type: 'LOGIN_SUCCESS'; payload: { user: User; token: string } }
  | { type: 'LOGIN_FAILURE'; payload: string }
  | { type: 'LOGOUT' }
  | { type: 'LOAD_USER'; payload: User }
  | { type: 'UPDATE_USER'; payload: User };

interface AuthContextType extends AuthState {
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => void;
  updateUser: (user: User) => void;
  refreshToken: () => Promise<void>;
  impersonate: (employeeId: string) => Promise<void>;
  stopImpersonating: () => Promise<void>;
  completeSsoLogin: (code: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const initialState: AuthState = {
  user: null,
  token: localStorage.getItem('token'),
  isAuthenticated: false,
  isLoading: true,
};

function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'LOGIN_START':
      return { ...state, isLoading: true };
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        isLoading: false,
      };
    case 'LOGIN_FAILURE':
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
      };
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
      };
    case 'LOAD_USER':
      return {
        ...state,
        user: action.payload,
        isAuthenticated: true,
        isLoading: false,
      };
    case 'UPDATE_USER':
      return {
        ...state,
        user: action.payload,
      };
    default:
      return state;
  }
}

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      dispatch({ type: 'LOGOUT' });
    } else {
      // Token exists but we don't validate it on mount anymore
      // User will need to login again if token is invalid
      dispatch({ type: 'LOGOUT' });
    }
  }, []);

  const login = async (credentials: LoginCredentials) => {
    dispatch({ type: 'LOGIN_START' });
    try {
      const { accessToken, user: loginUser } = await authService.login(credentials);
      localStorage.setItem('token', accessToken);

      const user = loginUser ?? await authService.getCurrentUser();
      if (!user) {
        throw new Error('Login response missing user profile');
      }

      dispatch({ type: 'LOGIN_SUCCESS', payload: { user, token: accessToken } });
      toast.success('Login successful!');
      if (user.mustChangePassword) {
        navigate('/change-password');
      } else {
        navigate(defaultHomePath(user));
      }
    } catch (error: any) {
      localStorage.removeItem('token');
      dispatch({ type: 'LOGIN_FAILURE', payload: error.message });
      toast.error(error.message || 'Login failed');
    }
  };

  const logout = () => {
    // Close any open FaceIO sessions/modals before logout
    try {
      // Remove any FaceIO modal elements from the DOM
      const fioElements = document.querySelectorAll('[id*="fio"], [class*="fio"]');
      fioElements.forEach(element => element.remove());
      
      // Try to access FaceIO instance from window and close session if available
      const faceioInstance = (window as any).faceio;
      if (faceioInstance && typeof faceioInstance.restartSession === 'function') {
        faceioInstance.restartSession().catch(() => {});
      }
    } catch (error) {
      console.log('FaceIO cleanup error (non-critical):', error);
    }
    
    localStorage.removeItem('token');
    sessionStorage.removeItem('adminToken');
    dispatch({ type: 'LOGOUT' });
    toast.success('Logged out successfully');
    navigate('/login');
  };

  const impersonate = async (employeeId: string) => {
    try {
      const currentToken = localStorage.getItem('token');
      if (currentToken && !state.user?.impersonatedBy) {
        sessionStorage.setItem('adminToken', currentToken);
      }
      const { user, accessToken } = await authService.impersonate(employeeId);
      localStorage.setItem('token', accessToken);
      dispatch({ type: 'LOGIN_SUCCESS', payload: { user, token: accessToken } });
      toast.success(`Now viewing as ${user.firstName} ${user.lastName}`);
      navigate(defaultHomePath(user));
    } catch (error: any) {
      toast.error(error.response?.data?.message || error.message || 'Unable to log in as employee');
    }
  };

  const stopImpersonating = async () => {
    const adminToken = sessionStorage.getItem('adminToken');
    if (!adminToken) {
      logout();
      return;
    }
    localStorage.setItem('token', adminToken);
    sessionStorage.removeItem('adminToken');
    try {
      const user = await authService.getCurrentUser();
      dispatch({ type: 'LOGIN_SUCCESS', payload: { user, token: adminToken } });
      toast.success('Returned to administrator session');
      navigate(defaultHomePath(user));
    } catch (error) {
      logout();
    }
  };

  const updateUser = (user: User) => {
    dispatch({ type: 'UPDATE_USER', payload: user });
  };

  const refreshToken = async () => {
    try {
      const { accessToken } = await authService.refreshToken();
      localStorage.setItem('token', accessToken);
      // Update token in state without changing user
      dispatch({ type: 'LOGIN_SUCCESS', payload: { user: state.user!, token: accessToken } });
    } catch (error) {
      logout();
    }
  };

  const completeSsoLogin = async (code: string) => {
    dispatch({ type: 'LOGIN_START' });
    try {
      const { accessToken, user: ssoUser } = await authService.exchangeSsoCode(code);
      localStorage.setItem('token', accessToken);

      const user = ssoUser ?? (await authService.getCurrentUser());
      if (!user) {
        throw new Error('SSO response missing user profile');
      }

      dispatch({ type: 'LOGIN_SUCCESS', payload: { user, token: accessToken } });
      toast.success('Signed in via Club Corners');
      if (user.mustChangePassword) {
        navigate('/change-password', { replace: true });
      } else {
        navigate(defaultHomePath(user), { replace: true });
      }
    } catch (error: any) {
      localStorage.removeItem('token');
      dispatch({ type: 'LOGIN_FAILURE', payload: error.message });
      throw error;
    }
  };

  const value: AuthContextType = {
    ...state,
    login,
    logout,
    updateUser,
    refreshToken,
    impersonate,
    stopImpersonating,
    completeSsoLogin,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 